import { navigate } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "gatsby";
import Layout from "../../components/layouts/layout";
import DataService from "../../services/api/data_services";
import { BASE_SITE_URL } from "../../shared/constants";
import ContactUsRightGrid from "../../templates/ContactUsRightGrid";
import "../../components/clientStoryBlog/index.scss";
import "../../components/skeletonLoader/index.scss";

const ClientStory = ({ pageContext: { clientstory } }) => {
  const [page, setPage] = useState(1);
  const [relatedStories, setRelatedStories] = useState([]);
  const [relatedRaw, setRelatedRaw] = useState({});
  const [content, setContent] = useState({});
  const formRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  const slug =
    typeof window !== "undefined" ? window.location.href.split("/")[4] : "";

  useEffect(() => {
    DataService.getData(
      `/admin/api/post?limit=3&page=${page}&page_id=1&type=Client Story&component=mini_view`,
    ).then((res) => {
      setRelatedStories((prev) => [...prev, ...res.data]);
      setRelatedRaw(res);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    DataService.getData(`/admin/api/post-details?slug=${slug}`)
      .then((res) => {
        setContent(res.data);
        setIsLoading(false);
      })
      .catch(() => navigate(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const keyPoints = [
    { title: content?.key_points_big, desc: content?.key_points },
    { title: content?.key_points_big1, desc: content?.key_points1 },
    { title: content?.key_points_big2, desc: content?.key_points2 },
  ].filter((kp) => kp.title);

  const hasForm = content?.show_contact_form === 1;
  const hasPdf = content?.pdf_file && content.pdf_file !== "NULL";

  const hasBody =
    content?.primary_description_1 ||
    content?.primary_description_2 ||
    content?.primary_description_3 ||
    content?.testimonial?.length > 0 ||
    content?.magic_box_image ||
    content?.video_url;

  return (
    <Layout
      seo={{
        title: content?.og_title || clientstory?.og_title,
        ogtitle: clientstory?.og_title,
        description: clientstory
          ? clientstory?.og_description
          : content?.og_description,
        ogurl: `${BASE_SITE_URL}/client-story/${clientstory?.slug}/`,
        ogimage: clientstory?.client_og_image,
      }}
    >
      {isLoading ? (
        <>
          {/* Hero skeleton */}
          <div className="csh-skl">
            <div className="csh-skl__inner">
              <div className="skl skl--dark csh-skl__back" />
              <div className="skl skl--dark csh-skl__badge" />
              <div className="skl skl--dark csh-skl__title-1" />
              <div className="skl skl--dark csh-skl__title-2" />
            </div>
          </div>
          {/* Body skeleton */}
          <div className="csb-skl">
            {[
              "full",
              "lg",
              "full",
              "md",
              "full",
              "lg",
              "full",
              "gap",
              "full",
              "md",
              "lg",
              "full",
              "sm",
              "gap",
              "full",
              "lg",
              "full",
              "md",
            ].map((s, i) =>
              s === "gap" ? (
                <div key={i} className="csb-skl__gap" />
              ) : (
                <div
                  key={i}
                  className={`skl csb-skl__line csb-skl__line--${s}`}
                />
              ),
            )}
          </div>
        </>
      ) : (
        <>
          {/* ============ 1. HERO ============ */}
          {content?.title && (
            <section className={`csh${!content.image ? " csh--no-img" : ""}`}>
              {content.image && (
                <div className="csh__bg-wrapper">
                  <img
                    src={content.image}
                    alt={content.title}
                    className="csh__bg-img"
                  />
                  <div className="csh__bg-overlay" />
                </div>
              )}
              <div className="csh__inner body-container">
                <Link to="/clients" className="csh__back">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  Our Clients
                </Link>
                <span className="csh__badge">Case Study</span>
                <h1 className="csh__title">{content.title}</h1>
                {content.banner_image_title &&
                  content.banner_image_title !== content.title && (
                    <p className="csh__tagline">{content.banner_image_title}</p>
                  )}
              </div>
            </section>
          )}

          {/* ============ 2. BODY ============ */}
          {hasBody && (
            <section className="csb">
              <div className="csb__inner body-container">
                {content?.primary_description_1 && (
                  <div
                    className="csb__desc"
                    dangerouslySetInnerHTML={{
                      __html: content.primary_description_1,
                    }}
                  />
                )}
                {content?.primary_description_2 && (
                  <div
                    className="csb__desc"
                    dangerouslySetInnerHTML={{
                      __html: content.primary_description_2,
                    }}
                  />
                )}
                {content?.primary_description_3 && (
                  <div
                    className="csb__desc"
                    dangerouslySetInnerHTML={{
                      __html: content.primary_description_3,
                    }}
                  />
                )}

                {/* Testimonials */}
                {content?.testimonial?.length > 0 && (
                  <div className="csb__testimonials">
                    {content.testimonial.map((t, i) => (
                      <blockquote key={i} className="csb__testimonial">
                        <p className="csb__testimonial-text">{t.content}</p>
                        <cite className="csb__testimonial-cite">
                          &mdash; {t.client_name}
                        </cite>
                      </blockquote>
                    ))}
                  </div>
                )}

                {/* Magic box image */}
                {content?.magic_box_image && (
                  <div className="csb__magic-img">
                    <img
                      src={content.magic_box_image}
                      alt={content.imageAltTxt || content.title}
                    />
                  </div>
                )}

                {/* Video */}
                {content?.video_url && content.video_url !== "NULL" && (
                  <div className="csb__video">
                    <video autoPlay playsInline loop muted>
                      <source src={content.video_url} type="video/mp4" />
                    </video>
                    {content?.video_description && (
                      <p className="csb__video-caption">
                        {content.video_description}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ============ 3. KEY POINTS ============ */}
          {(keyPoints.length > 0 || content?.secondary_description) && (
            <section className="csk">
              <div className="body-container">
                {content?.secondary_title && (
                  <h2 className="csk__heading">{content.secondary_title}</h2>
                )}
                {keyPoints.length > 0 && (
                  <div className="csk__grid">
                    {keyPoints.map((kp, i) => (
                      <div key={i} className="csk__card">
                        <p className="csk__card-title">{kp.title}</p>
                        {kp.desc && <p className="csk__card-desc">{kp.desc}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {content?.secondary_description && (
                  <div
                    className="csk__secondary-desc"
                    dangerouslySetInnerHTML={{
                      __html: content.secondary_description,
                    }}
                  />
                )}
              </div>
            </section>
          )}

          {/* ============ 4. ACCESS SECTION ============ */}
          {(hasForm || hasPdf) && (
            <section className="csa" ref={formRef} id="cs-access">
              <div className="body-container">
                {hasForm ? (
                  <div className="csa__split">
                    <div className="csa__text">
                      <span className="csa__eyebrow">Get the Full Story</span>
                      <h2 className="csa__heading">
                        {content.banner_image_title || content.title}
                      </h2>
                      <p className="csa__sub">
                        Fill in your details below to get full access to this
                        case study.
                      </p>
                    </div>
                    <div className="csa__form-wrapper">
                      <ContactUsRightGrid
                        bannerTitle={content.banner_image_title}
                        pdfFile={content.pdf_file}
                        articleid={content.id}
                        type="client-story"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="csa__download">
                    <div className="csa__download-icon">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                    </div>
                    <h2 className="csa__download-heading">
                      Read the Full Case Study
                    </h2>
                    <p className="csa__download-sub">
                      Download the complete case study to explore the full
                      journey and results.
                    </p>
                    <a
                      href={content.pdf_file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="csa__download-btn"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download Case Study
                    </a>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ============ 5. RELATED STORIES ============ */}
          {relatedStories.length > 0 && (
            <section className="csr">
              <div className="body-container">
                <h2 className="csr__heading">
                  Related{" "}
                  <span className="csr__heading-accent">Case Studies</span>
                </h2>
                <div className="csr__grid">
                  {relatedStories.map((story, i) => (
                    <a
                      key={story.slug || i}
                      href={`/client-story/${story.slug}/`}
                      className="csr__card"
                    >
                      {story.og_image && (
                        <div className="csr__card-img-wrapper">
                          <img
                            src={story.og_image}
                            alt={story.title}
                            className="csr__card-img"
                          />
                        </div>
                      )}
                      <div className="csr__card-content">
                        {story.article_tag && (
                          <span className="csr__card-tag">
                            {story.article_tag}
                          </span>
                        )}
                        <h3 className="csr__card-title">{story.title}</h3>
                        {story.og_description && (
                          <p className="csr__card-desc">
                            {story.og_description}
                          </p>
                        )}
                        <span className="csr__card-read">
                          Read Case Study →
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
                {relatedRaw.has_more && (
                  <div className="csr__load-more">
                    <button
                      className="csr__load-more-btn"
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Load More Case Studies
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ============ 6. CTA STRIP ============ */}
          <section className="csc">
            <div className="body-container csc__inner">
              <div className="csc__text">
                <h2 className="csc__heading">
                  Ready to Transform Your Business?
                </h2>
                <p className="csc__sub">
                  Let&apos;s discuss how DigiRyte can accelerate your digital
                  capabilities.
                </p>
              </div>
              <a href="/contact-us" className="csc__btn">
                Get in Touch
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </section>
        </>
      )}
    </Layout>
  );
};

export default ClientStory;
